import type { Contact } from '~api/address_book';

import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DropdownMenu, InteractiveTable, Text } from '@ingrid/luterkort-react';

import { RoleGuard } from '~src/components/RoleGuard/RoleGuard';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { AppRoute } from '~src/main/routes';

import { useAddressBookRemoveMutation } from '../mutations';

interface Props {
  displayEditAction?: boolean;
  contact: Contact;
  trigger: ReactNode;
}

export function Menu(props: Props) {
  const removeMutation = useAddressBookRemoveMutation();

  const { contact, displayEditAction, trigger } = props;

  const { setHighlightedKey } = InteractiveTable.useContext();
  const { t } = useTranslation();
  const { toast } = useToast();

  async function handleDelete() {
    const removePromise = removeMutation.mutateAsync({ contact_ids: [contact.id!] });

    await toast.promise(removePromise, {
      success: { title: t('addressBook.delete.success') },
      error: { title: t('addressBook.delete.error') },
      loading: { title: t('addressBook.delete.loading') },
    });

    setHighlightedKey(null);
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>{trigger}</DropdownMenu.Trigger>

      <DropdownMenu.Content>
        {displayEditAction && (
          <RoleGuard roles={['transport:edit']} withTooltip>
            <DropdownMenu.Item asChild>
              <RouterLink to={`${AppRoute.EDIT}/${contact.id}`}>{t('common.edit')}</RouterLink>
            </DropdownMenu.Item>
          </RoleGuard>
        )}
        <RoleGuard roles={['transport:edit']} withTooltip>
          <DropdownMenu.Item asChild>
            <RouterLink to={`${AppRoute.DUPLICATE}/${contact.id}`}>{t('common.duplicate')}</RouterLink>
          </DropdownMenu.Item>
        </RoleGuard>
        <RoleGuard roles={['transport:edit']} withTooltip>
          <DropdownMenu.Item onClick={handleDelete}>
            <Text variant="critical">{t('common.delete')}</Text>
          </DropdownMenu.Item>
        </RoleGuard>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
